import axios, { InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  CustomerPlanningProject,
  CreateTimeEntry,
  CreateProjectTask,
  CreateTimeProfile,
  PaginatedResult,
  TimeCustomerLookup,
  TimeEntry,
  TimeEntryFilter,
  TimeProfile,
  TimeProjectLookup,
  TimeProjectPlanningOverview,
  TimeProjectTaskLookup,
  TimeReportFilter,
  TimeSummaryReport,
  TimeWeeklyReport,
  UpdateTimeEntry,
  UpdateProjectTask,
  UpdateTimeProfile,
  TimeApiKey,
  CreateTimeApiKey,
  UpdateTimeApiKey,
  CreatedTimeApiKey,
  TimeLead,
  TimeLeadFilter,
  CreateTimeLead,
  UpdateTimeLead,
  TimeLeadTag,
  TimeActivity,
  CreateTimeActivity,
  UpdateTimeActivity,
  CopyDayRequest,
  CopyDayResult,
  CopyDayOperation,
  PersonalDashboardRead,
} from '../types';
import { RoleViewMode } from '../types/auth';
import { getApiBaseUrl } from './config';
import { ROLE_VIEW_MODE_STORAGE_KEY, TIME_VIEW_MODE_HEADER } from './roleViewMode';
import { assertAuthAccount, authAccountFromToken } from './authAccount';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Local continuity check only; never transmitted as ownership or scope. */
    expectedAuthAccount?: string;
  }
}

const API_BASE_URL = getApiBaseUrl();
export const AUTH_EXPIRED_EVENT = 'devrain-time-auth-expired';

type AuthRefreshHandler = () => Promise<string | null>;
type RetryableRequestConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

let authRefreshHandler: AuthRefreshHandler | null = null;
let authRefreshPromise: Promise<string> | null = null;

export const setAuthRefreshHandler = (handler: AuthRefreshHandler | null) => {
  authRefreshHandler = handler;
};

const expireAuthentication = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('authUser');
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

const tokenNeedsRefresh = (token: string) => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return typeof exp !== 'number' || exp <= Date.now() / 1000;
  } catch {
    return true;
  }
};

const refreshAuthToken = () => {
  if (authRefreshPromise) {
    return authRefreshPromise;
  }

  if (!authRefreshHandler) {
    expireAuthentication();
    return Promise.reject(new Error('Authentication refresh is unavailable'));
  }

  const initialToken = localStorage.getItem('token');
  authRefreshPromise = authRefreshHandler()
    .then((token) => {
      if (!token) {
        throw new Error('Authentication refresh failed');
      }

      const currentToken = localStorage.getItem('token');
      // A late refresh must not replace a different account or revive a logout.
      if (currentToken === initialToken || (currentToken && authAccountFromToken(currentToken) === authAccountFromToken(token))) {
        localStorage.setItem('token', token);
      }
      return token;
    })
    .catch((error) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken === initialToken || (currentToken && authAccountFromToken(currentToken) === authAccountFromToken(initialToken))) {
        expireAuthentication();
      }
      throw error;
    })
    .finally(() => {
      authRefreshPromise = null;
    });

  return authRefreshPromise;
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface TimeRequestOptions {
  viewMode?: RoleViewMode;
}

api.interceptors.request.use(async (config) => {
  let token = localStorage.getItem('token');
  if (config.expectedAuthAccount !== undefined) assertAuthAccount(config.expectedAuthAccount, token);
  if (token && tokenNeedsRefresh(token)) {
    token = await refreshAuthToken();
  }
  if (config.expectedAuthAccount !== undefined) assertAuthAccount(config.expectedAuthAccount, token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const explicitViewMode = config.headers[TIME_VIEW_MODE_HEADER];
  if (explicitViewMode === 'Admin') {
    delete config.headers[TIME_VIEW_MODE_HEADER];
  } else if (explicitViewMode === 'User' || explicitViewMode === 'ReportViewer') {
    config.headers[TIME_VIEW_MODE_HEADER] = explicitViewMode;
  } else {
    const storedViewMode = localStorage.getItem(ROLE_VIEW_MODE_STORAGE_KEY);
    if (storedViewMode === 'User' || storedViewMode === 'ReportViewer') {
      config.headers[TIME_VIEW_MODE_HEADER] = storedViewMode;
    } else {
      delete config.headers[TIME_VIEW_MODE_HEADER];
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const requestConfig = error.config as RetryableRequestConfig | undefined;
      if (requestConfig?.expectedAuthAccount !== undefined) assertAuthAccount(requestConfig.expectedAuthAccount);
      if (!requestConfig || requestConfig._authRetry || !authRefreshHandler) {
        expireAuthentication();
        return Promise.reject(error);
      }

      requestConfig._authRetry = true;
      const refreshedToken = await refreshAuthToken();
      if (requestConfig.expectedAuthAccount !== undefined) assertAuthAccount(requestConfig.expectedAuthAccount, refreshedToken);
      requestConfig.headers.Authorization = `Bearer ${refreshedToken}`;
      return api(requestConfig);
    }
    return Promise.reject(error);
  }
);

const cleanParams = <T extends object>(params: T) => {
  return Object.fromEntries(
    Object.entries(params as Record<string, unknown>).filter(([, value]) =>
      value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)
    )
  );
};

const serializeRepeatedParams = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();
  Object.entries(cleanParams(params)).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => searchParams.append(key, String(item)));
      return;
    }

    searchParams.append(key, String(value));
  });
  return searchParams.toString();
};

const reportRequestConfig = (filter: TimeReportFilter = {}) => ({
  params: cleanParams(filter),
  paramsSerializer: { serialize: serializeRepeatedParams },
});

const requestOptionsConfig = (options?: TimeRequestOptions) => ({
  headers: options?.viewMode ? { [TIME_VIEW_MODE_HEADER]: options.viewMode } : undefined,
});

const entryRequestConfig = (filter: TimeEntryFilter = {}, options?: TimeRequestOptions) => ({
  params: cleanParams(filter),
  ...requestOptionsConfig(options),
});

const reportRequestConfigWithOptions = (filter: TimeReportFilter = {}, options?: TimeRequestOptions) => ({
  ...reportRequestConfig(filter),
  ...requestOptionsConfig(options),
});

export const timeProfilesAPI = {
  getProfiles: (includeInactive = true, options?: TimeRequestOptions): Promise<TimeProfile[]> =>
    api.get('/time-profiles', { params: { includeInactive }, ...requestOptionsConfig(options) }).then(res => res.data),
  createProfile: (profile: CreateTimeProfile): Promise<TimeProfile> =>
    api.post('/time-profiles', profile).then(res => res.data),
  updateProfile: (id: number, profile: UpdateTimeProfile): Promise<TimeProfile> =>
    api.put(`/time-profiles/${id}`, profile).then(res => res.data),
  deleteProfile: (id: number): Promise<void> =>
    api.delete(`/time-profiles/${id}`).then(res => res.data),
};

export const timeApiKeysAPI = {
  getMyKeys: (): Promise<TimeApiKey[]> =>
    api.get('/api-keys').then(res => res.data),
  createMyKey: (key: CreateTimeApiKey): Promise<CreatedTimeApiKey> =>
    api.post('/api-keys', key).then(res => res.data),
  updateMyKey: (id: number, key: UpdateTimeApiKey): Promise<TimeApiKey> =>
    api.put(`/api-keys/${id}`, key).then(res => res.data),
  revokeMyKey: (id: number): Promise<void> =>
    api.delete(`/api-keys/${id}`).then(res => res.data),
  getAllKeys: (): Promise<TimeApiKey[]> =>
    api.get('/admin/api-keys').then(res => res.data),
  updateAnyKey: (id: number, key: UpdateTimeApiKey): Promise<TimeApiKey> =>
    api.put(`/admin/api-keys/${id}`, key).then(res => res.data),
  revokeAnyKey: (id: number): Promise<void> =>
    api.delete(`/admin/api-keys/${id}`).then(res => res.data),
};

export const timeEntriesAPI = {
  copyDay: (command: CopyDayRequest, expectedAuthAccount: string): Promise<CopyDayResult> =>
    api.post('/time-entries/copy-day', command, { expectedAuthAccount }).then(res => res.data),
  getCopyDayOperation: (operationId: string, expectedAuthAccount: string): Promise<CopyDayOperation> =>
    api.get(`/time-entries/copy-day-operations/${operationId}`, { expectedAuthAccount }).then(res => res.data),
  getDashboard: (range: { startDate: string; endDate: string }, expectedAuthAccount?: string): Promise<PersonalDashboardRead> =>
    api.get('/time-entries/dashboard', { params: range, expectedAuthAccount }).then(res => res.data),
  getEntries: (filter: TimeEntryFilter = {}, options?: TimeRequestOptions): Promise<PaginatedResult<TimeEntry>> =>
    api.get('/time-entries', entryRequestConfig(filter, options)).then(res => res.data),
  getDeletedEntries: (filter: TimeEntryFilter = {}): Promise<PaginatedResult<TimeEntry>> =>
    api.get('/time-entries/deleted', entryRequestConfig(filter)).then(res => res.data),
  createEntry: (entry: CreateTimeEntry): Promise<TimeEntry> =>
    api.post('/time-entries', entry).then(res => res.data),
  updateEntry: (id: number, entry: UpdateTimeEntry): Promise<TimeEntry> =>
    api.put(`/time-entries/${id}`, entry).then(res => res.data),
  deleteEntry: (id: number): Promise<void> =>
    api.delete(`/time-entries/${id}`).then(res => res.data),
};

export const timeReportsAPI = {
  getSummary: (filter: TimeReportFilter = {}, options?: TimeRequestOptions): Promise<TimeSummaryReport> =>
    api.get('/time-reports/summary', reportRequestConfigWithOptions(filter, options)).then(res => res.data),
  getDetailed: (filter: TimeReportFilter = {}, options?: TimeRequestOptions): Promise<PaginatedResult<TimeEntry>> =>
    api.get('/time-reports/detailed', reportRequestConfigWithOptions(filter, options)).then(res => res.data),
  getWeekly: (filter: TimeReportFilter = {}, options?: TimeRequestOptions): Promise<TimeWeeklyReport[]> =>
    api.get('/time-reports/weekly', reportRequestConfigWithOptions(filter, options)).then(res => res.data),
  exportDetailed: async (filter: TimeReportFilter = {}): Promise<Blob> => {
    const response = await api.post('/time-reports/export/excel', cleanParams(filter), { responseType: 'blob' });
    return response.data;
  },
  exportDetailedPdf: async (filter: TimeReportFilter = {}): Promise<Blob> => {
    const response = await api.post('/time-reports/export/pdf', cleanParams(filter), { responseType: 'blob' });
    return response.data;
  },
};

export const projectTasksAPI = {
  getTasks: (projectId?: number, includeInactive = true, search?: string): Promise<TimeProjectTaskLookup[]> =>
    api.get('/time-project-tasks', { params: cleanParams({ projectId, includeInactive, search }) }).then(res => res.data),
  createTask: (task: CreateProjectTask): Promise<TimeProjectTaskLookup> =>
    api.post('/time-project-tasks', task).then(res => res.data),
  updateTask: (id: number, task: UpdateProjectTask): Promise<TimeProjectTaskLookup> =>
    api.put(`/time-project-tasks/${id}`, task).then(res => res.data),
  deleteTask: (id: number): Promise<void> =>
    api.delete(`/time-project-tasks/${id}`).then(res => res.data),
};

export const timeProjectsAPI = {
  updateProjectColor: (id: number, color?: string): Promise<TimeProjectLookup> =>
    api.put(`/time-projects/${id}/color`, { color }).then(res => res.data),
};

export const timeProjectPlanningAPI = {
  getOverview: (includeHidden = false): Promise<TimeProjectPlanningOverview> =>
    api.get('/time-project-planning', { params: { includeHidden } }).then(res => res.data),
  updateMemberProjects: (timeProfileId: number, projectIds: number[]): Promise<void> =>
    api.put(`/time-project-planning/${timeProfileId}`, { projectIds }).then(res => res.data),
  updateMemberVisibility: (timeProfileId: number, isHiddenFromProjectPlanning: boolean): Promise<void> =>
    api.put(`/time-project-planning/${timeProfileId}/visibility`, { isHiddenFromProjectPlanning }).then(res => res.data),
};

export const customerProjectPlanningAPI = {
  getProjects: (includeAll = false): Promise<CustomerPlanningProject[]> =>
    api.get('/time-customer-project-planning', { params: { includeAll } }).then(res => res.data),
  updateVisibility: (projectId: number, showInProjectPlanning: boolean): Promise<void> =>
    api.put(`/time-customer-project-planning/${projectId}/visibility`, { showInProjectPlanning }).then(res => res.data),
  updateStatus: (projectId: number, planningStatus: string | null): Promise<void> =>
    api.put(`/time-customer-project-planning/${projectId}/status`, { planningStatus }).then(res => res.data),
};

export const timeLeadsAPI = {
  getLeads: (filter: TimeLeadFilter = {}): Promise<PaginatedResult<TimeLead>> =>
    api.get('/time-leads', {
      params: cleanParams(filter),
      paramsSerializer: { serialize: serializeRepeatedParams },
    }).then(res => res.data),
  getLead: (id: number): Promise<TimeLead> =>
    api.get(`/time-leads/${id}`).then(res => res.data),
  getTags: (): Promise<TimeLeadTag[]> =>
    api.get('/time-leads/tags').then(res => res.data),
  createLead: (lead: CreateTimeLead): Promise<TimeLead> =>
    api.post('/time-leads', lead).then(res => res.data),
  updateLead: (id: number, lead: UpdateTimeLead): Promise<TimeLead> =>
    api.put(`/time-leads/${id}`, lead).then(res => res.data),
  getActivities: (leadId: number): Promise<TimeActivity[]> =>
    api.get(`/time-leads/${leadId}/activities`).then(res => res.data),
  createActivity: (leadId: number, activity: CreateTimeActivity): Promise<TimeActivity> =>
    api.post(`/time-leads/${leadId}/activities`, activity).then(res => res.data),
  updateActivity: (leadId: number, activityId: number, activity: UpdateTimeActivity): Promise<TimeActivity> =>
    api.put(`/time-leads/${leadId}/activities/${activityId}`, activity).then(res => res.data),
  deleteActivity: (leadId: number, activityId: number): Promise<void> =>
    api.delete(`/time-leads/${leadId}/activities/${activityId}`).then(res => res.data),
};

export const timeCustomersAPI = {
  getCustomers: (search?: string, excludeArchived?: boolean): Promise<TimeCustomerLookup[]> =>
    api.get('/time-customers', { params: cleanParams({ search, excludeArchived }) }).then(res => res.data),
  createCustomer: (title: string): Promise<TimeCustomerLookup> =>
    api.post('/time-customers', { title }).then(res => res.data),
  updateCustomer: (id: number, title: string): Promise<TimeCustomerLookup> =>
    api.put(`/time-customers/${id}`, { id, title }).then(res => res.data),
  deleteCustomer: (id: number): Promise<void> =>
    api.delete(`/time-customers/${id}`).then(res => res.data),
};

export const timeLookupsAPI = {
  getProjects: (customerId?: number, search?: string): Promise<TimeProjectLookup[]> =>
    api.get('/time-lookups/projects', { params: cleanParams({ customerId, search }) }).then(res => res.data),
  getProjectTasks: (projectId?: number, search?: string): Promise<TimeProjectTaskLookup[]> =>
    api.get('/time-lookups/project-tasks', { params: cleanParams({ projectId, search }) }).then(res => res.data),
  getCustomers: (search?: string): Promise<TimeCustomerLookup[]> =>
    api.get('/time-lookups/customers', { params: cleanParams({ search }) }).then(res => res.data),
};
